import * as React from 'react';
import { FaPlus, FaCalendarAlt, FaChevronLeft, FaChevronRight, FaMagic, FaTrashAlt, FaDownload, FaUpload } from 'react-icons/fa';
import dateUtil from '../../../utils/dateUtil';
import { getCalendarWeek } from './weeklyShiftsUtils';
import styles from './WeeklyShifts.module.css';

interface WeeklyShiftsHeaderProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  startDate: Date;
  setStartDate: (d: Date) => void;
  calendarBtnRef: React.RefObject<HTMLButtonElement>;
  setShowCalendar: (v: boolean) => void;
  setCalendarPos: (pos: { top: number; left: number }) => void;
  handleAutoAssignClick: () => void;
  handleClearAllClick: () => void;
  showAddShift: boolean;
  setShowAddShift: (v: boolean) => void;
  setShiftData: (fn: any) => void;
  lastShiftDefaults: { start_time_time: string; end_time_time: string; work_area: string };
  workAreaOptions: any[];
  renderLocationFilter: () => React.ReactNode;
  renderWAFilterDND: () => React.ReactNode;
  renderTTWAFilter: () => React.ReactNode;
  renderAbsenceWAFilter: () => React.ReactNode;
}

export function WeeklyShiftsHeader({
  activeTab,
  setActiveTab,
  startDate,
  setStartDate,
  calendarBtnRef,
  setShowCalendar,
  setCalendarPos,
  handleAutoAssignClick,
  handleClearAllClick,
  setShowAddShift,
  setShiftData,
  lastShiftDefaults,
  workAreaOptions,
  renderLocationFilter,
  renderWAFilterDND,
  renderTTWAFilter,
  renderAbsenceWAFilter,
}: WeeklyShiftsHeaderProps) {
  const CALENDAR_WIDTH = 320;

  const tabs = [
    { key: 'shifts', label: 'Shifts' },
    { key: 'timeTrackings', label: 'Time Tracking' },
    { key: 'absences', label: 'Absences' },
    { key: 'availabilities', label: 'Availabilities' },
    { key: 'dashboard', label: 'Dashboard' },
  ];

  const openAddShift = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    const workAreaToUse =
      lastShiftDefaults.work_area || (workAreaOptions.length > 0 ? workAreaOptions[0].id : '');
    setShiftData({
      start_time_date: todayStr,
      start_time_time: lastShiftDefaults.start_time_time,
      end_time_time: lastShiftDefaults.end_time_time,
      employees_needed: 1,
      work_area: workAreaToUse,
      notes: '',
      weekdays: [],
      interval: 1,
      repeat_until: '',
      never_ends: false,
      crm_task: null,
      crm_task_title: '',
    });
    setShowAddShift(true);
  };

  const openCalendar = () => {
    if (calendarBtnRef.current) {
      const rect = calendarBtnRef.current.getBoundingClientRect();
      let left = rect.left + window.scrollX;
      if (left + CALENDAR_WIDTH > window.innerWidth) {
        left = window.innerWidth - CALENDAR_WIDTH - 12;
        if (left < 0) left = 8;
      }
      setCalendarPos({ top: rect.bottom + window.scrollY + 6, left });
    }
    setShowCalendar(true);
  };

  const goPrevWeek = () => {
    const monday = dateUtil.getMonday(startDate);
    const prevMonday = new Date(monday);
    prevMonday.setDate(prevMonday.getDate() - 7);
    setStartDate(prevMonday);
  };

  const goNextWeek = () => {
    const monday = dateUtil.getMonday(startDate);
    const nextMonday = new Date(monday);
    nextMonday.setDate(nextMonday.getDate() + 7);
    setStartDate(nextMonday);
  };

  return (
    <div className={`glass-header ${styles.glassHeader}`}>
      {/* Row 1: Tabs + Week Navigation */}
      <div className={styles.headerRow}>
        <div data-testid="ws-tabs" className={styles.tabNav}>
          {tabs.map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                data-testid={`ws-tab-${tab.key}`}
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ''}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'shifts' && (
          <div className={styles.weekNav}>
            <button
              className={styles.weekNavBtn}
              onClick={goPrevWeek}
              aria-label="Previous week"
            >
              <FaChevronLeft className={styles.iconSmall} />
            </button>
            <button
              ref={calendarBtnRef}
              onClick={openCalendar}
              className={styles.cwButton}
            >
              <FaCalendarAlt className={styles.calendarIconSmall} />
              KW {getCalendarWeek(dateUtil.getMonday(startDate))}
            </button>
            <button
              className={styles.weekNavBtn}
              onClick={goNextWeek}
              aria-label="Next week"
            >
              <FaChevronRight className={styles.iconSmall} />
            </button>
          </div>
        )}
      </div>

      {/* Row 2: Tab-specific controls */}
      {activeTab === 'shifts' && (
        <div className={styles.shiftRow2}>
          <div className={styles.locationFilterWrap}>{renderLocationFilter()}</div>
          <div className={styles.waFilterCenter}>{renderWAFilterDND()}</div>
          <div className={styles.actionButtons}>
            <button
              onClick={handleAutoAssignClick}
              className={styles.autoAssignBtn}
              title="Auto-Assign Shifts"
            >
              <FaMagic className={styles.calendarIconSmall} />
            </button>
            <button
              onClick={handleClearAllClick}
              className={styles.clearAllBtn}
              title="Delete all assignments"
            >
              <FaTrashAlt className={styles.calendarIconSmall} />
            </button>
            <button
              onClick={openAddShift}
              className={styles.addShiftBtn}
              data-testid="add-shift-button"
            >
              <FaPlus className={styles.iconXSmall} />
              Shift
            </button>
          </div>
        </div>
      )}

      {activeTab === 'timeTrackings' && renderTTWAFilter()}
      {activeTab === 'absences' && renderAbsenceWAFilter()}
      {activeTab === 'availabilities' && <div className={styles.availabilitiesPlaceholder} />}
    </div>
  );
}
